import styled, { css } from 'styled-components';

import { mediaUp, rem } from 'approot/shared/style';
import {
  grey4,
  grey5,
  purple1,
  purple5,
  purple7,
} from 'approot/shared/styles/colours.style';
import {
  borderRadius8,
  standardFocusState,
} from 'approot/shared/styles/borders.style';
import {
  spacing8,
  spacing16,
  spacing24,
} from 'approot/shared/styles/spacing.style';
import { ComponentPlusLine, ComponentPlusWrapper } from '../components.style';
import {
  FLEX_SPACE_DISTRIBUTE,
  FLEX_SPACE_APART,
  ContentFlexAlignment,
} from 'global/constants';
import { ColourItem } from 'approot/shared/colour/colour-token.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { flexCentreAlignCss } from 'approot/shared/styles/flex.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { ComponentViewWrapper } from '../components-view-wrapper.style';

type ColumnsContainerProps = {
  $colours?: ColourItem | null;
  $showBackground?: boolean;
  $showBorder?: boolean;
  $marginBottom?: number;
  $turnOffResponsive?: boolean;
};

export const ColumnsContainer = styled.div<ColumnsContainerProps>`
  border-radius: ${rem(borderRadius8)};
  display: flex;
  margin-left: -${rem(spacing8)};
  margin-right: -${rem(spacing8)};
  margin-bottom: ${({ $marginBottom = spacing24 }) => rem($marginBottom)};

  ${({ $turnOffResponsive }) =>
    $turnOffResponsive
      ? css`
          flex-direction: row;
        `
      : css`
          @media screen {
            flex-direction: column;

            ${mediaUp.md`
              flex-direction: row;
            `}
          }
        `}

  ${({ $showBorder, $showBackground }) =>
    ($showBorder || $showBackground) &&
    `
    margin-left: 0;
    margin-right: 0;
    padding: ${rem(spacing8)} 0;
  `}

  ${({ $colours, $showBackground }) =>
    $colours &&
    $showBackground &&
    `
    background-color: ${$colours.colour1};
  `}

  ${({ $colours, $showBorder }) =>
    $colours &&
    $showBorder &&
    `
    border: 1px solid ${$colours.colour3};
  `}
`;

export const ColumnInner = styled.div`
  border-radius: ${rem(borderRadius8)};
  margin: 0 ${rem(spacing8)};
`;

export const ColumnWrapper = styled.div`
  border-radius: ${rem(borderRadius8)};

  ${ComponentViewWrapper}:last-child {
    margin-bottom: 0;
  }
`;

type ColumnsColumnProps = {
  $colspan: number;
  $alignment: ContentFlexAlignment;
} & ColumnsContainerProps;

export const ColumnsColumn = styled.div<ColumnsColumnProps>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(spacing16)};
  justify-content: ${({ $alignment }) => $alignment};
  padding: 0 ${rem(spacing8)};

  ${({ $alignment }) =>
    $alignment === FLEX_SPACE_DISTRIBUTE &&
    `
    ${ColumnInner} {
      height: 100%;
    }
    ${ColumnWrapper} {
      display: flex;
      flex-direction: column;
      height: 100%;
      & > div {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }
  `};

  ${({ $alignment }) =>
    $alignment === FLEX_SPACE_APART &&
    `
      ${ColumnInner} {
        height: 100%;
      }
      ${ColumnWrapper} {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
      }
  `};

  ${({ $showBorder, $showBackground }) =>
    ($showBorder || $showBackground) &&
    `
    ${ColumnWrapper} {
      padding: ${rem(spacing8)}
    }
  `}

  ${({ $colours, $showBackground }) =>
    $colours &&
    $showBackground &&
    `
    ${ColumnWrapper} {
      background-color: ${$colours.colour1};
    }
  `}

  ${({ $colours, $showBorder }) =>
    $colours &&
    $showBorder &&
    `
    ${ColumnWrapper} {
      border: 1px solid ${$colours.colour3};
    }
  `}

  @media print {
    width: ${({ $colspan }) => ($colspan / 12) * 100}%;
  }

  ${({ $turnOffResponsive, $colspan }) =>
    $turnOffResponsive
      ? css`
          margin-bottom: 0;
          width: ${($colspan / 12) * 100}%;
        `
      : css<ColumnsColumnProps>`
          ${mediaUp.md<ColumnsColumnProps>`
            margin-bottom: 0;
            width: ${({ $colspan }) => ($colspan / 12) * 100}%;
          `}
        `}
`;

type ColumnsColumnEditProps = {
  'data-selector'?: string;
};
export const ColumnsColumnEdit = styled(
  ColumnsColumn
).attrs<ColumnsColumnEditProps>({
  'data-selector': 'columns-column-edit',
})`
  padding: 0;
  position: relative;
`;

type ColumnEmptyProps = {
  $isActive?: boolean;
};
export const ColumnEmpty = styled(ColumnWrapper)<ColumnEmptyProps>`
  ${flexCentreAlignCss}

  border: 1px dashed ${grey4};
  border-radius: ${rem(borderRadius8)};
  position: absolute;
  left: ${rem(spacing8)};
  right: ${rem(spacing8)};
  top: 0;
  bottom: 0;
  margin: 0;

  ${({ $isActive }) =>
    $isActive &&
    `
    background-color: ${purple1};
    border: 1px solid ${purple5};
  `}

  & > ${SecondaryButton} {
    z-index: 1;
  }
`;

type ColumnsContainerEditProps = {
  $isColumnsEmpty?: boolean;
};
export const ColumnsContainerEdit = styled(
  ColumnsContainer
)<ColumnsContainerEditProps>`
  ${({ $isColumnsEmpty }) =>
    $isColumnsEmpty &&
    `
    ${ColumnsColumnEdit} {
      min-height: ${rem(140)};
    }
`}
`;

export const ColumnAddMenuWrapper = styled(ComponentPlusWrapper)`
  ${ComponentPlusLine} {
    left: ${rem(spacing8)};
    right: ${rem(spacing8)};
    width: auto;
  }
`;

export const ColumnsTooltipWrapper = styled(TooltipWrapper)`
  display: block;
  position: static;
`;

export const ColumnsHandle = styled.button`
  ${standardFocusState}

  display: none;
  flex-shrink: 0;
  cursor: ew-resize;
  border: none;
  padding: 0;
  margin: 0 ${rem(spacing8)};
  width: ${rem(8)};
  height: ${rem(80)};
  background-color: ${grey5};
  position: absolute;
  right: -${rem(12)};
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  border-radius: ${rem(72)};

  &:hover,
  &:active {
    background-color: ${purple7};
  }

  ${mediaUp.lg`
    display: block;
  `}
`;
